import type { VgAnswerValue, VgAnswers, VgSection } from '../../../types/vorstellungsgespraech';
import { BedarfeInReichweiteSection } from './BedarfeInReichweiteSection';
import { VgFieldInput } from './VgFieldInput';

interface VgSectionCardProps {
  section: VgSection;
  index: number;
  answers: VgAnswers;
  onChange: (key: string, value: VgAnswerValue) => void;
  disabled?: boolean;
  /** First name of the logged-in interviewer, substituted for the [Name] placeholder. */
  interviewerName?: string;
  /** Needed by widget sections that look something up for this candidate. */
  bewerberId: string;
}

/**
 * One step of the interview script. Instructions are things the interviewer reads out
 * loud, questions capture the candidate's answers, and widgets show what the answers
 * so far imply.
 */
export function VgSectionCard({
  section,
  index,
  answers,
  onChange,
  disabled = false,
  interviewerName,
  bewerberId,
}: VgSectionCardProps) {
  const number = (
    <span className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-xs font-medium text-gray-600">
      {index}
    </span>
  );

  if (section.kind === 'instruction') {
    return (
      <section className="rounded-lg border border-primary-100 bg-primary-50/60 p-4">
        <div className="flex items-start gap-3">
          {number}
          <div className="flex-1">
            <h2 className="text-sm font-semibold text-primary-900">{section.title}</h2>
            {section.body && (
              <div className="mt-1.5 space-y-1.5 text-sm text-primary-800/90">
                {section.body.map((line, i) => (
                  <p key={i}>
                    {interviewerName ? line.replace(/\[Name\]/g, interviewerName) : line}
                  </p>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    );
  }

  if (section.kind === 'widget') {
    return (
      <section className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex items-start gap-3">
          {number}
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-semibold text-gray-900">{section.title}</h2>
            {section.hint && <p className="mt-1 text-xs text-gray-500">{section.hint}</p>}

            <div className="mt-3">
              <BedarfeInReichweiteSection
                bewerberId={bewerberId}
                answers={answers}
                disabled={disabled}
                collapsible
              />
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-start gap-3">
        {number}
        <div className="flex-1 min-w-0">
          <h2 className="text-sm font-semibold text-gray-900">{section.title}</h2>
          {section.hint && <p className="mt-1 text-xs text-gray-500">{section.hint}</p>}

          <div className="mt-3 space-y-3">
            {section.fields.map((field) => (
              <VgFieldInput
                key={field.key}
                field={field}
                value={answers[field.key]}
                onChange={(value) => onChange(field.key, value)}
                disabled={disabled}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
